import styled, { CSSObject } from '@emotion/styled'
import { IconComment } from '../../icons/IconComment/IconComment'
import { IconPremium } from '../../icons/IconPremium/IconPremium'
import { EditorialLabel } from '../../typography/EditorialLabel/EditorialLabel'
import { Breakpoints } from '../../__styling/settings/breakpoints'
import { colors } from '../../__styling/settings/colors'
import { fonts } from '../../__styling/settings/fonts'
import { perthNowSectionDefinitions } from '../../__styling/settings/sections/perthnow'
import { zIndex } from '../../__styling/settings/z-index'
import {
    breakpoint,
    breakpointMax,
    getSmallestBreakpointValue,
} from '../../__styling/style-functions'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { transition } from '../../__styling/style-mixins'
import { themedValue } from '../../__styling/themed-value'
import {
    KickerBreakpoints,
    KickerPosition,
    KickerSizeOptions,
    KickerType,
    KickerVisibility,
} from './Kicker'

export interface StyledKickerProps {
    kickerVisibility?: KickerVisibility
    kickerPosition?: KickerPosition
    size?: KickerSizeOptions
    kickerType?: KickerType
}

export type BreakpointKey = keyof Breakpoints
export const kickerBreakpoints: KickerBreakpoints[] = [
    'initial',
    'xs',
    'sm',
    'md',
]

export function getVisibilityStyles({
    kickerVisibility,
}: StyledKickerProps): CSSObject {
    if (kickerVisibility === 'hidden') {
        return { display: 'none' }
    }

    if (!kickerVisibility || kickerVisibility === 'visible') {
        return { display: 'inline-block' } // default
    }

    return (
        kickerVisibility &&
        Object.entries(kickerVisibility)
            .slice(1)
            .reduce<CSSObject>(
                (styles, [breakpointValue, visibilityForBreakpoint]) => {
                    styles[breakpoint(breakpointValue as BreakpointKey)] = {
                        display:
                            visibilityForBreakpoint === 'hidden'
                                ? 'none'
                                : 'inline-block',
                    }

                    return styles
                },
                {},
            )
    )
}

export const StyledKicker = styled('span')<StyledKickerProps>(
    getVisibilityStyles,
    ({ kickerVisibility, kickerPosition, kickerType, size, theme }) => ({
        [breakpointMax('xs')]: {
            display:
                getSmallestBreakpointValue(kickerVisibility) === 'hidden'
                    ? 'none'
                    : 'inline-block',
        },
        lineHeight: 1,
        verticalAlign: 'top',
        marginBottom:
            kickerPosition === 'bottomleftinset-herovideo'
                ? 0
                : theme.margins.xs,
        textTransform: themedValue(theme, {
            perthnow: undefined,
            fallback: 'uppercase',
        }),
        fontFamily: themedValue(theme, {
            perthnow: fonts.perthnow.sansSerif,
            thewest: fonts.thewest.sansSerif,
            sevennews: fonts.sevennews.sansSerifCond,
        }),
        fontSize: themedValue(theme, {
            perthnow: size === 'large' ? calcRem(16) : calcRem(13),
            thewest: calcRem(11),
            sevennews: calcRem(12),
        }),
        fontWeight: themedValue(theme, {
            perthnow: 600,
            thewest: 500,
            sevennews: 700,
        }),
        color: themedValue(theme, {
            perthnow: colors.white,
            thewest:
                kickerType === 'text'
                    ? colors.thewest.greyElephant
                    : colors.white,
            sevennews: (section) =>
                kickerType === 'text'
                    ? section.cards.kicker.primaryColor
                    : colors.white,
        }),
        padding: themedValue(theme, {
            perthnow: size === 'large' ? calcRem(7) : calcRem(4),
            sevennews: kickerType === 'block' ? calcRem(4, 9, 3) : undefined,
            thewest: kickerType === 'block' ? calcRem(5, 10) : undefined,
        }),
        backgroundColor: themedValue(theme, {
            perthnow: perthNowSectionDefinitions.default.primaryColor,
            sevennews:
                kickerType === 'block'
                    ? (section) => section.cards.kicker.primaryColor
                    : undefined,
            thewest:
                kickerType === 'block' ? colors.thewest.westblue : undefined,
        }),
        zIndex: themedValue(theme, {
            sevennews: zIndex.sevennews.cardKicker,
            fallback: undefined,
        }),
        ...transition({ properties: ['background, color'] }),
    }),
    ({ kickerPosition }) => {
        switch (kickerPosition) {
            case 'topleft':
                return {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                }
            case 'bottomleft':
                return {
                    position: 'absolute',
                    marginTop: calcRem(-34),
                }
            case 'bottomleftinset-hero': {
                return {
                    position: 'relative',
                    marginTop: calcRem(-43),
                    left: 0,
                }
            }
            case 'bottomleftinset-herovideo': {
                return {
                    position: 'absolute',
                    left: calcRem(16),
                    bottom: calcRem(16),
                }
            }
            case 'mobilebottomleft-insetdesktop':
                return {
                    position: 'relative',
                    marginTop: calcRem(-43),
                    left: 0,

                    [breakpoint('sm')]: {
                        left: calcRem(16),
                    },
                }
            case 'bottomleftinset':
                return {
                    position: 'relative',
                    marginTop: calcRem(-43),
                    left: calcRem(16),
                }

            case 'topleftinset':
                return {
                    position: 'absolute',
                    top: calcRem(16),
                    left: calcRem(16),
                }
            default:
                return {}
        }
    },
)

export const StyledKickerText = styled('span')()

export interface StyledPremiumIconProps {
    width: number
    height: number
}

export const StyledPremiumIcon = styled(IconPremium)<StyledPremiumIconProps>(
    ({ width, height }) => ({
        width,
        height,
        verticalAlign: 'text-bottom',
        transform: 'translate(2px, 1px)',
    }),
)

export const StyledCommentsIcon = styled(IconComment)({
    marginLeft: calcRem(4),
    transform: 'translate(0, 3px)',
    width: 13,
    height: 12,
    fill: colors.thewest.greyHippo,
    strokeWidth: 0,
})

/**
 * This editorial label only appears on The West next to kickers
 */
export const StyledEditorialLabel = styled(EditorialLabel)({
    display: 'inline-block',
    paddingRight: calcRem(8),

    '&::after': {
        content: `''`,
        display: 'inline-block',
        width: 1,
        height: calcRem(13),
        transform: 'translate(4px, 2px)',
        background: colors.thewest.greyElephant,
    },
})
